import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { DocumentDetailEditor } from './DocumentDetailEditor';
import { Document } from '../DocumentList/DocumentList';
import { useDocumentsApi } from '../../hooks/useDocumentsApi';

export interface DocumentViewerProps {
  document?: Document;
  onDocumentChange?: (doc: Document) => void;
}

export const DocumentViewer: React.FC<DocumentViewerProps> = ({ document, onDocumentChange }) => {
  const { presignedUrl } = useDocumentsApi();
  const [url, setUrl] = useState<string>();
  const [loading, setLoading] = useState(false);
  const lastDocIdRef = useRef<string | undefined>(undefined);
  const { t } = useTranslation();

  useEffect(() => {
    if (!document) {
      // Clear preview when no document is selected to avoid flicker
      lastDocIdRef.current = undefined;
      setUrl(undefined);
      setLoading(false);
      return;
    }

    if (lastDocIdRef.current === document.id) {
      // Document has not changed; avoid re-fetching URL to prevent flicker
      return;
    }

    const currentId = document.id;
    lastDocIdRef.current = currentId;
    setLoading(true);
    presignedUrl(currentId)
      .then((u) => {
        // Ignore late responses for outdated selections
        if (lastDocIdRef.current === currentId) {
          setUrl(u);
        }
      })
      .finally(() => {
        if (lastDocIdRef.current === currentId) {
          setLoading(false);
        }
      });
  }, [document?.id]);

  if (!document) {
    return (
      <Card>
        <CardContent className="p-12 text-center text-muted-foreground">
          {t('empty.selectDocument')}
        </CardContent>
      </Card>
    );
  }
  return (
    <Card>
      <CardContent className="p-4">
        <Tabs defaultValue="preview" className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-custom-grey p-1 h-10 items-center justify-center rounded-lg text-muted-foreground">
            <TabsTrigger
              value="preview"
              className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm h-full rounded-md"
            >
              Aperçu du document
            </TabsTrigger>
            <TabsTrigger
              value="details"
              className="data-[state=active]:bg-white data-[state=active]:text-primary data-[state=active]:shadow-sm h-full rounded-md"
            >
              Détails du document
            </TabsTrigger>
          </TabsList>
          <TabsContent value="preview" className="mt-4">
            {loading && (
              <div className="w-full h-[80vh] flex items-center justify-center text-muted-foreground">
                {t('empty.loading')}
              </div>
            )}
            {!loading &&
              url &&
              (document.mimeType?.includes('pdf') ? (
                <iframe
                  src={`https://docs.google.com/viewerng/viewer?url=${encodeURIComponent(url)}&embedded=true`}
                  title={document.name}
                  className="w-full h-[80vh]"
                />
              ) : (
                <img src={url} alt={document.name} className="object-contain w-full max-h-[80vh]" />
              ))}
          </TabsContent>
          <TabsContent value="details" className="mt-4">
            <div className="w-full max-w-full overflow-hidden">
              <DocumentDetailEditor
                documentId={document.id}
                metadata={document.metadata}
                documentType={document.documentType}
                onSaved={(meta) => {
                  if (onDocumentChange) {
                    onDocumentChange({ ...document, metadata: meta });
                  }
                }}
              />
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
